import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {createProductServiceRepository} from "../data/repositories";
import {ProductServiceOrderSummary} from "../domain/product-service.types";
import {PaginationMeta} from "../../../redux/service/i-service";
import {GET_STORE_SERVICES_ORDERS_RQ_KEY} from "../../../react-query-hooks/store/services/constants";
import {GetStoreServicesOrdersApiResponse} from "../../../react-query-hooks/store/services/get-orders/types";

type LegacyOrder = GetStoreServicesOrdersApiResponse["orders"][number];

/**
 * Maps an order summary to the legacy orders-list item shape. The card reads
 * only firstName/lastName/amount/currency/delivery_date/createdAt/statusId and
 * productService.name; the rest are defaulted (the detail drawer fetches the
 * full order separately). `productService` must be truthy — the list skips
 * rows without it.
 */
const toLegacyOrder = (s: ProductServiceOrderSummary): LegacyOrder => ({
  id: s.id,
  userId: 0,
  productServiceId: 0,
  firstName: s.firstName,
  lastName: s.lastName,
  emailAddress: s.emailAddress,
  currency: s.currency,
  amount: String(s.amount),
  charges: "0",
  reference: "",
  processor_reference: null,
  statusId: s.statusId,
  is_paid: true,
  revision_requested: false,
  payment_channel: "paystack",
  discount_code: null,
  is_discounted: false,
  charge_bearer: "customer",
  booking_questions: null,
  delivery_date: s.deliveryDate ?? "",
  createdAt: s.createdAt,
  updatedAt: s.updatedAt,
  productService: {
    id: 0,
    userId: 0,
    name: s.productServiceName,
    slug: "",
    tiny_url: "",
    description: "",
    delivery_timeframe_unit: "days",
    delivery_timeframe_value: 1,
    hideFromProfile: false,
    url_redirect: null,
    statusId: 0,
    request_phone_number: false,
    booking_questions: null,
    createdAt: "",
    updatedAt: "",
    deleted_at: null,
  },
});

/**
 * Legacy-compatible coach orders list hook. Preserves the params, response
 * shape, query key, and refetch behavior of the original, while routing through
 * the feature repository (always v2).
 *
 * @deprecated Use useProductServiceOrders from features/product-services instead.
 */
export const useGetStoreServicesOrders = ({
  statusId,
  page = 1,
  per_page = 5,
}: {
  statusId: number;
  page: number;
  per_page: number;
}) => {
  const repository = createProductServiceRepository();

  return useQuery<
    {data: GetStoreServicesOrdersApiResponse; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_STORE_SERVICES_ORDERS_RQ_KEY, {statusId, page}],
    queryFn: async () => {
      const result = await repository.getOrders({statusId, page, perPage: per_page});
      const orders = result.data.map(toLegacyOrder);

      if ("meta" in result) {
        const {currentPage, perPage, lastPage} = result.meta;
        return {
          data: {orders},
          meta: {
            current_page: currentPage,
            next_page: currentPage < lastPage ? currentPage + 1 : 0,
            per_page: perPage,
            previous_page: currentPage > 1 ? currentPage - 1 : 0,
            total_pages: lastPage,
          },
        };
      }

      return {
        data: {orders},
        meta: {
          current_page: page,
          next_page: result.hasNextPage ? page + 1 : 0,
          per_page,
          previous_page: page > 1 ? page - 1 : 0,
          total_pages: Math.ceil(result.total / per_page),
        },
      };
    },
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  });
};
